import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import { formatCurrency, formatDateTime, EMPTY } from '../../shared/utils/format';
import { Badge, Button, LoadingSkeleton, Pagination } from '@nexora/design-system';
import './obligations.css';

/* ───────────────────────────────────────────────────────────────────────────
   Painel de Obrigações Fiscais — /obligations.
   Consome: GET /obligations/dashboard (KPIs), GET /obligations (lista
   paginada: tipoObrigacao, periodoApuracao, obrigacaoArquivo, statusSefaz,
   valorIcms/Ipi/PisCofins, status) e PATCH /obligations/:id/status (ação
   "Avançar" percorre PENDENTE → EM_ANDAMENTO → VALIDADA → ASSINADA →
   TRANSMITIDA → CONCLUIDA). Período de apuração vem 'YYYY-MM'; exibimos
   MM/YYYY sem maquiar (não é data ISO completa).
   ─────────────────────────────────────────────────────────────────────────── */

type ObrigacaoStatus =
  | 'PENDENTE'
  | 'EM_ANDAMENTO'
  | 'VALIDADA'
  | 'ASSINADA'
  | 'TRANSMITIDA'
  | 'CONCLUIDA'
  | 'COM_ERROS';

interface ObrigacaoFiscal {
  id: string;
  tipoObrigacao: string;
  periodoApuracao: string;
  obrigacaoArquivo: string;
  statusSefaz: string;
  valorIcms: number;
  valorIpi: number;
  valorPisCofins: number;
  status: ObrigacaoStatus;
  ocorreuEm: string | null;
}

interface ObligationsDashboard {
  totalIcms: number;
  totalIpi: number;
  totalPisCofins: number;
  totalTributos: number;
  countPendentes: number;
  countTransmitidas: number;
  countComErros: number;
  totalObrigacoes: number;
  guiasPendentes: number;
  totalGuiasPendentesValor: number;
  guiasVencidas: number;
}

interface PaginatedObrigacoes {
  data: ObrigacaoFiscal[];
  total: number;
  totalPages: number;
}

const TIPO_LABEL: Record<string, string> = {
  FISCAL: 'SPED Fiscal (EFD ICMS/IPI)',
  CONTABIL: 'ECD (Contábil)',
  CONTRIBUICOES: 'EFD Contribuições',
};

const STATUS_LABEL: Record<string, string> = {
  PENDENTE: 'Pendente',
  EM_ANDAMENTO: 'Em andamento',
  VALIDADA: 'Validada',
  ASSINADA: 'Assinada',
  TRANSMITIDA: 'Transmitida',
  CONCLUIDA: 'Concluída',
  COM_ERROS: 'Com erros',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral' | 'info'> = {
  PENDENTE: 'warning',
  EM_ANDAMENTO: 'info',
  VALIDADA: 'info',
  ASSINADA: 'neutral',
  TRANSMITIDA: 'success',
  CONCLUIDA: 'success',
  COM_ERROS: 'error',
};

const FLUXO: ObrigacaoStatus[] = ['PENDENTE', 'EM_ANDAMENTO', 'VALIDADA', 'ASSINADA', 'TRANSMITIDA', 'CONCLUIDA'];

function periodoDisplay(periodo: string): string {
  return periodo ? periodo.replace('-', '/') : EMPTY;
}

export function ObligationsDashboardPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const queryClient = useQueryClient();

  const [tipoFilter, setTipoFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const dashQuery = useQuery({
    queryKey: ['obligations', tenantId, 'dashboard'],
    queryFn: () => apiClient.get<ObligationsDashboard>('/obligations/dashboard'),
    enabled: !!tenantId,
  });

  const listQuery = useQuery({
    queryKey: ['obligations', tenantId, 'list', tipoFilter, page],
    queryFn: () =>
      apiClient.get<PaginatedObrigacoes>('/obligations', {
        params: {
          page: String(page),
          limit: String(pageSize),
          ...(tipoFilter !== 'ALL' ? { tipo: tipoFilter } : {}),
        },
      }),
    enabled: !!tenantId,
  });

  const advanceMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ObrigacaoStatus }) =>
      apiClient.patch<ObrigacaoFiscal>(`/obligations/${id}/status`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['obligations', tenantId] });
    },
  });

  const dash = dashQuery.data;
  const { data, isLoading, error } = listQuery;
  const obrigacoes = data?.data ?? [];
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  const proximoStatus = (status: ObrigacaoStatus): ObrigacaoStatus | undefined => {
    const idx = FLUXO.indexOf(status);
    return idx >= 0 && idx < FLUXO.length - 1 ? FLUXO[idx + 1] : undefined;
  };

  const kpis = dash
    ? [
        { key: 'icms', label: 'ICMS a recolher', value: formatCurrency(dash.totalIcms), tone: 'ok', icon: 'account_balance', hint: `PIS/COFINS ${formatCurrency(dash.totalPisCofins)}` },
        { key: 'ipi', label: 'IPI a recolher', value: formatCurrency(dash.totalIpi), tone: 'info', icon: 'inventory_2', hint: `Total de tributos ${formatCurrency(dash.totalTributos)}` },
        { key: 'pendentes', label: 'Obrigações pendentes', value: String(dash.countPendentes), tone: dash.countComErros > 0 ? 'bad' : 'info', icon: 'pending_actions', hint: `${dash.countTransmitidas} transmitidas · ${dash.countComErros} com erros` },
        { key: 'guias', label: 'Guias pendentes', value: `${dash.guiasPendentes} · ${formatCurrency(dash.totalGuiasPendentesValor)}`, tone: dash.guiasVencidas > 0 ? 'bad' : 'muted', icon: 'receipt_long', hint: `${dash.guiasVencidas} vencidas` },
      ]
    : [];

  return (
    <div className="obl-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Obrigações Fiscais</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Livros fiscais (SPED), período de apuração e status junto à SEFAZ.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-md">
        {isLoading && !dash && <LoadingSkeleton count={4} height="108px" />}
        {kpis.map((k) => (
          <div key={k.key} className="obl-kpi">
            <div className="obl-kpi__top">
              <span className="material-symbols-outlined" aria-hidden="true">
                {k.icon}
              </span>
              <span className="obl-kpi__label">{k.label}</span>
            </div>
            <div className={`obl-kpi__value obl-kpi--${k.tone}`}>{k.value}</div>
            <div className="obl-kpi__hint">{k.hint}</div>
          </div>
        ))}
      </div>

      <div className="obl-tabs" role="tablist" aria-label="Tipo de obrigação">
        {[{ value: 'ALL', label: 'Todos' }, ...Object.entries(TIPO_LABEL).map(([value, label]) => ({ value, label }))].map((tab) => (
          <button
            key={tab.value}
            role="tab"
            type="button"
            aria-selected={tipoFilter === tab.value}
            className={tipoFilter === tab.value ? 'obl-tab is-active' : 'obl-tab'}
            onClick={() => {
              setTipoFilter(tab.value);
              setPage(1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="obl-card">
        <div className="obl-card__head">
          <h2 className="obl-card__title">Livros fiscais</h2>
          <span className="obl-count">{data?.total ?? 0} obrigações</span>
        </div>

        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="obl-state">
            <p className="obl-state__title">Erro ao carregar obrigações</p>
            <p className="obl-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void listQuery.refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && obrigacoes.length === 0 && (
          <div className="obl-state">
            <span className="material-symbols-outlined obl-state__icon" aria-hidden="true">
              description
            </span>
            <p className="obl-state__detail">Nenhuma obrigação fiscal encontrada.</p>
          </div>
        )}

        {!isLoading && !error && obrigacoes.length > 0 && (
          <>
            <div className="obl-table">
              <table>
                <thead>
                  <tr>
                    <th>Livro Fiscal (SPED)</th>
                    <th>Período de Apuração</th>
                    <th>Obrigação (Arquivo)</th>
                    <th>Status SEFAZ</th>
                    <th style={{ textAlign: 'right' }}>ICMS (R$)</th>
                    <th style={{ textAlign: 'right' }}>IPI (R$)</th>
                    <th style={{ textAlign: 'right' }}>PIS/COFINS (R$)</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {obrigacoes.map((obl) => {
                    const proximo = proximoStatus(obl.status);
                    return (
                      <tr key={obl.id}>
                        <td>
                          {TIPO_LABEL[obl.tipoObrigacao] ?? obl.tipoObrigacao}
                          <span className="obl-sub">{obl.ocorreuEm ? formatDateTime(obl.ocorreuEm) : EMPTY}</span>
                        </td>
                        <td className="obl-mono">{periodoDisplay(obl.periodoApuracao)}</td>
                        <td className="obl-nowrap">{obl.obrigacaoArquivo}</td>
                        <td>{obl.statusSefaz || EMPTY}</td>
                        <td className="obl-num">{formatCurrency(obl.valorIcms)}</td>
                        <td className="obl-num">{formatCurrency(obl.valorIpi)}</td>
                        <td className="obl-num">{formatCurrency(obl.valorPisCofins)}</td>
                        <td>
                          <Badge variant={STATUS_VARIANT[obl.status] ?? 'neutral'} size="sm">
                            {STATUS_LABEL[obl.status] ?? obl.status}
                          </Badge>
                        </td>
                        <td className="obl-actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!proximo || advanceMutation.isPending}
                            onClick={() => proximo && advanceMutation.mutate({ id: obl.id, status: proximo })}
                          >
                            Avançar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="obl-footer">
              <span className="obl-count">
                Página {page} de {totalPages}
              </span>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}